import React from 'react';
import { Tag } from 'primereact/tag';
import { PollQuestionWithVotesResponse } from '../../../../../../api/types/students/course-poll-feedbacks/course-poll-with-questions';
import { PollQuestionType } from '../../../../../../api/types/settings/settings-all';

export interface PollQuestionCardProps {
  question: PollQuestionWithVotesResponse;
}

// Helper function to calculate average for a multiple choice question
const calculateQuestionAverage = (
  question: PollQuestionWithVotesResponse,
): number | null => {
  if (question.questionType !== PollQuestionType.multiple_choice) {
    return null; // Free text questions don't have numeric averages
  }

  if (question.totalResponses === 0) {
    return null; // No responses
  }

  // Calculate: sum(optionValue * voteCount) / totalResponses
  const totalValue = question.options.reduce(
    (sum, option) => sum + option.optionValue * option.voteCount,
    0,
  );

  return totalValue / question.totalResponses;
};

const formatAverage = (average: number | null): string => {
  if (average === null) {
    return 'אין נתונים';
  }
  return average.toFixed(2);
};

export const PollQuestionCard: React.FC<PollQuestionCardProps> = ({
  question,
}) => {
  const questionAverage = calculateQuestionAverage(question);

  return (
    <div
      style={{
        padding: '12px',
        backgroundColor: '#f9fafb',
        borderRadius: '6px',
        border: '1px solid #e5e7eb',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '12px',
        }}
      >
        <div style={{ flex: 1 }}>
          <h5
            style={{
              margin: '0 0 8px 0',
              color: '#374151',
              fontSize: '16px',
            }}
          >
            {question.questionOrder + 1}. {question.questionText}
          </h5>
          <div
            style={{
              fontSize: '13px',
              color: '#6b7280',
            }}
          >
            <span>
              <strong>תגובות:</strong> {question.totalResponses}
            </span>
            {questionAverage !== null && (
              <span style={{ marginRight: '16px' }}>
                {' '}
                | <strong>ממוצע:</strong>{' '}
                <span
                  style={{
                    color: '#059669',
                    fontWeight: 'bold',
                  }}
                >
                  {formatAverage(questionAverage)}
                </span>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Multiple Choice Options */}
      {question.questionType === PollQuestionType.multiple_choice && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            marginTop: '12px',
          }}
        >
          {question.options
            .slice()
            .sort((a, b) => a.optionOrder - b.optionOrder)
            .map((option) => (
              <div
                key={option.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  border: '1px solid #e5e7eb',
                }}
              >
                <div style={{ flex: 1 }}>
                  <span style={{ color: '#374151' }}>{option.optionText}</span>
                  <span
                    style={{
                      marginRight: '8px',
                      color: '#6b7280',
                      fontSize: '12px',
                    }}
                  >
                    (ערך: {option.optionValue})
                  </span>
                </div>
                <Tag
                  value={`${option.voteCount} הצבעות`}
                  severity="info"
                  style={{ fontSize: '12px' }}
                />
              </div>
            ))}
        </div>
      )}

      {/* Free Text Answers */}
      {question.questionType === PollQuestionType.free_text && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            marginTop: '12px',
          }}
        >
          {question.freeTextAnswers.length > 0 ? (
            question.freeTextAnswers.map((answer, aIndex) => (
              <div
                key={aIndex}
                style={{
                  padding: '10px 12px',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  border: '1px solid #e5e7eb',
                  color: '#374151',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {answer}
              </div>
            ))
          ) : (
            <div
              style={{
                padding: '10px 12px',
                color: '#9ca3af',
                fontStyle: 'italic',
              }}
            >
              אין תשובות טקסט חופשי
            </div>
          )}
        </div>
      )}
    </div>
  );
};
